import { API_PAGE_CHECK_PATH, API_PREFIX_BLOCK_CONTENT_PATH, API_PREFIX_PAGE_PATH } from "@/constants/prefix-path-api"
import { message } from "@/lib/config/lib/antd-global"
import { AxiosRequestConfig } from "axios"
import { t } from "i18next"
import { useState } from "react"
import { HttpClient } from "../../../../app/api/HttpClient"
import { dataPayloadBlockContent, dataPayloadPage, FilterSearchParams } from "../../ui-builder/config/types"

type PageListQueryParams = Omit<FilterSearchParams, "populate"> & {
  fields?: string[]
  populate?: string | Record<string, any>
}

type PageByIdQueryParams = {
  fields?: string[]
  populate?: string | Record<string, any>
  publicationState?: string
  locale?: string
}

const DEFAULT_PAGE_BY_ID_QUERY_PARAMS: PageByIdQueryParams = {
  populate: "*",
}

const pageService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const addPage = async (data: { data: dataPayloadPage }) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(`${API_PREFIX_PAGE_PATH}`, data)
      return response
    } catch (error) {
      message.error(t("Không thể thêm trang. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPageById = async (
    id: number,
    queryParams: PageByIdQueryParams = DEFAULT_PAGE_BY_ID_QUERY_PARAMS,
  ) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}/${id}`, { params: queryParams })
      return response?.data || null
    } catch (error) {
      message.error(t("Lỗi khi tải dữ liệu trang. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updatePage = async (id: number, data: { data: dataPayloadPage }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.put(`${API_PREFIX_PAGE_PATH}/${id}`, data)
      message.success(t("Trang được cập nhật thành công!"))
      return response
    } catch (error) {
      message.error(t("Không thể cập nhật trang. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deletePage = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.delete(`${API_PREFIX_PAGE_PATH}/${id}`)
      return response
    } catch (error) {
      message.error(t("Không thể xóa trang. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  // Lấy path của các page ĐÃ PUBLISH trong danh sách id, trong MỘT query (tránh
  // N+1). Chỉ published vì bản nháp chưa từng public → không có cache để purge.
  const fetchPublishedPathsByIds = async (ids: number[]): Promise<string[]> => {
    if (ids.length === 0) return []
    try {
      const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, {
        params: {
          fields: ["path"],
          filters: { id: { $in: ids }, publishedAt: { $notNull: true } },
          locale: "all",
          pagination: { pageSize: ids.length },
        },
      })
      return (response?.data ?? [])
        .map((p: { attributes?: { path?: string } }) => p?.attributes?.path ?? "")
        .filter(Boolean)
    } catch {
      return []
    }
  }

  const fetchAllPages = async (searchTerms: { [key: string]: string } = {}) => {
    const params: PageListQueryParams = {
      fields: ["name", "domain", "locale", "createdAt", "publishedAt"],
      populate: {
        localizations: {
          fields: ["locale"],
        },
      },
      sort: "createdAt:desc",
      publicationState: "preview",
      filters: {},
      pagination: {
        page: "1",
        pageSize: "20",
      },
      locale: "all",
    }

    const paginationKeys = ["page", "pageSize", "start", "limit"]

    Object.keys(searchTerms).forEach(key => {
      const value = searchTerms[key]
      if (!value) return

      if (key === "publicationState") {
        const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
        params.publicationState = convertParamStatusPost

        if (value === "draft") {
          params.filters.publishedAt = { $null: true }
        } else {
          delete params.filters.publishedAt
        }
      } else if (key === "sort") {
        params.sort = value
      } else if (key === "locale") {
        params.locale = value
      } else if (paginationKeys.includes(key)) {
        params.pagination = {
          ...params.pagination,
          [key]: value.toString(),
        }
      } else {
        params.filters[key] = { $containsi: value }
      }
    })

    try {
      const response = await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, { params })
      const modifiedResponse = response.data.map((page: any) => ({
        ...page,
        status: page.attributes.publishedAt ? "published" : "draft",
        publishedAt: page.attributes.publishedAt,
      }))

      return {
        data: modifiedResponse,
        meta: response.meta,
      }
    } catch (error) {
      message.error(t("Lỗi khi tải danh sách trang. Vui lòng thử lại"))
    }
  }

  const checkSlug = async (slug: string) => {
    const params: AxiosRequestConfig["params"] = {
      filters: {
        slug: { $eq: slug },
      },
      publicationState: "preview",
    }

    return await HttpClient.get(`${API_PREFIX_PAGE_PATH}`, { params })
  }

  /**
   * Ask the backend whether (path, locale) is free. `path` is derived server-side
   * from slug + parentId — pass either an explicit `path`, or `slug` (+ optional
   * `parentId`) and let the API compute it.
   * Returns { path, locale, available } from GET /page/check-path.
   */
  const checkPath = async (input: {
    slug?: string
    path?: string
    parentId?: number | null
    locale?: string
    excludeId?: number
  }): Promise<{ path: string; locale: string; available: boolean } | null> => {
    const params: Record<string, string | number> = {}
    if (input.path) params.path = input.path
    if (input.slug) params.slug = input.slug
    if (input.parentId != null) params.parentId = input.parentId
    if (input.locale) params.locale = input.locale
    if (input.excludeId != null) params.excludeId = input.excludeId

    const response = await HttpClient.get<{ data: { path: string; locale: string; available: boolean } }>(
      API_PAGE_CHECK_PATH,
      { params },
    )
    return response?.data ?? null
  }

  /**
   * List pages usable as a parent: same domain + locale, excluding a given id.
   * Returns raw Strapi rows ({ id, attributes: { name, slug, path, ... } }).
   */
  const fetchParentOptions = async (input: {
    domain?: string
    locale?: string
    excludeId?: number
  }) => {
    const filters: Record<string, unknown> = {}
    if (input.domain) filters.domain = { $eq: input.domain }
    if (input.excludeId != null) filters.id = { $ne: input.excludeId }

    const params: Record<string, unknown> = {
      fields: ["name", "slug", "path", "domain", "locale"],
      filters,
      populate: { parent: { fields: ["id"] } },
      publicationState: "preview",
      locale: input.locale || "vi",
      sort: "path:asc",
      pagination: { pageSize: 200 },
    }

    try {
      const response = await HttpClient.get<{ data: any[] }>(`${API_PREFIX_PAGE_PATH}`, { params })
      return response?.data ?? []
    } catch {
      return []
    }
  }

  const createLocalization = async (id: number, data: { data: dataPayloadPage & { locale?: string } }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const payload = data.data
      const response = await HttpClient.post(`${API_PREFIX_PAGE_PATH}/${id}/localizations`, payload)
      message.success(t("Tạo bản dịch thành công!"))
      return response
    } catch (error) {
      console.error("Tạo bản dịch lỗi:", error)
      message.error(t("Không thể tạo bản dịch. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const createLocalizationBlockContent = async (
    id: number,
    data: { data: dataPayloadBlockContent & { locale?: string } },
  ) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const payloadBlockContent = {
        ...data.data,
      }

      const response = await HttpClient.post(
        `${API_PREFIX_BLOCK_CONTENT_PATH}/${id}/localizations`,
        payloadBlockContent,
      )
      message.success(t("Tạo bản dịch nội dung khối thành công!"))
      return response
    } catch (error) {
      console.error("Tạo bản dịch lỗi:", error)
      message.error(t("Không thể tạo bản dịch nội dung khối. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    addPage,
    fetchPageById,
    updatePage,
    deletePage,
    fetchPublishedPathsByIds,
    fetchAllPages,
    checkSlug,
    checkPath,
    fetchParentOptions,
    createLocalization,
    createLocalizationBlockContent,
  }
}

export default pageService
